import {
	File,
	FileAudio,
	FileImage,
	FileText,
	FileVideo,
	X,
} from "lucide-react";
import { type SyntheticEvent, useRef } from "react";
import { useAttachmentAddress } from "@/hooks/use-attachment-address";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Image } from "./image";

export type FileAttachmentData = {
	id?: string;
	fileName: string;
	fileSize: number;
	fileType: string;
	/** The address a public object was stored with. A private one has none and opens through the server. */
	url?: string | null;
	objectKey?: string;
};

type FileAttachmentProps = {
	file: FileAttachmentData;
	onRemove?: () => void;
	className?: string;
	showRemove?: boolean;
};

function formatFileSize(bytes: number): string {
	if (bytes === 0) return "0 B";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(k));
	return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function getFileIcon(fileType: string) {
	if (fileType.startsWith("image/")) return FileImage;
	if (fileType.startsWith("video/")) return FileVideo;
	if (fileType.startsWith("audio/")) return FileAudio;
	if (
		fileType.includes("pdf") ||
		fileType.includes("document") ||
		fileType.includes("text")
	)
		return FileText;
	return File;
}

function isImageFile(fileType: string): boolean {
	return fileType.startsWith("image/");
}

function isVideoFile(fileType: string): boolean {
	return fileType.startsWith("video/");
}

function isAudioFile(fileType: string): boolean {
	return fileType.startsWith("audio/");
}

export function FileAttachment({
	file,
	onRemove,
	className,
	showRemove = false,
}: FileAttachmentProps) {
	const FileIcon = getFileIcon(file.fileType);
	// Until there is somewhere to open the file, it shows as a plain card.
	const { address, openFresh, renewIfExpired } = useAttachmentAddress(file);
	// Where playback was when a media element's address expired under it, to
	// carry on from there once the fresh address has loaded.
	const resumeAt = useRef<number | null>(null);
	const onMediaError = (event: SyntheticEvent<HTMLMediaElement>) => {
		if (renewIfExpired()) resumeAt.current = event.currentTarget.currentTime;
	};
	const onMediaLoaded = (event: SyntheticEvent<HTMLMediaElement>) => {
		if (resumeAt.current === null) return;
		event.currentTarget.currentTime = resumeAt.current;
		resumeAt.current = null;
	};

	if (address && isImageFile(file.fileType)) {
		return (
			<div className={cn("group relative inline-block", className)}>
				<a
					className="block"
					href={address}
					onClick={openFresh}
					rel="noopener noreferrer"
					target="_blank"
				>
					<Image
						alt={file.fileName}
						className="max-h-64 max-w-xs rounded-lg object-cover"
						height={256}
						onError={renewIfExpired}
						src={address}
						width={320}
					/>
				</a>
				{showRemove && onRemove && (
					<Button
						className="-top-2 -right-2 absolute size-6 opacity-0 transition-opacity group-hover:opacity-100"
						onClick={onRemove}
						size="icon"
						variant="destructive"
					>
						<X className="size-3" />
					</Button>
				)}
			</div>
		);
	}

	if (address && isVideoFile(file.fileType)) {
		return (
			<div className={cn("group relative", className)}>
				<video
					className="max-h-64 max-w-xs rounded-lg"
					controls
					onError={onMediaError}
					onLoadedMetadata={onMediaLoaded}
					src={address}
				>
					<track kind="captions" />
				</video>
				{showRemove && onRemove && (
					<Button
						className="-top-2 -right-2 absolute size-6 opacity-0 transition-opacity group-hover:opacity-100"
						onClick={onRemove}
						size="icon"
						variant="destructive"
					>
						<X className="size-3" />
					</Button>
				)}
			</div>
		);
	}

	if (address && isAudioFile(file.fileType)) {
		return (
			<div
				className={cn(
					"group relative flex items-center gap-2 rounded-lg border bg-muted/30 p-2",
					className
				)}
			>
				<FileIcon className="size-8 text-muted-foreground" />
				<div className="min-w-0 flex-1">
					<a
						className="block truncate font-medium text-sm hover:underline"
						href={address}
						onClick={openFresh}
						rel="noopener noreferrer"
						target="_blank"
					>
						{file.fileName}
					</a>
					<audio
						className="mt-1 h-8 w-full"
						controls
						onError={onMediaError}
						onLoadedMetadata={onMediaLoaded}
						src={address}
					>
						<track kind="captions" />
					</audio>
				</div>
				{showRemove && onRemove && (
					<Button
						className="size-6 shrink-0"
						onClick={onRemove}
						size="icon"
						variant="ghost"
					>
						<X className="size-3" />
					</Button>
				)}
			</div>
		);
	}

	return (
		<div
			className={cn(
				"group relative flex items-center gap-2 rounded-lg border bg-muted/30 p-2",
				className
			)}
		>
			<FileIcon className="size-8 shrink-0 text-muted-foreground" />
			<div className="min-w-0 flex-1">
				{address ? (
					<a
						className="block truncate font-medium text-sm hover:underline"
						href={address}
						onClick={openFresh}
						rel="noopener noreferrer"
						target="_blank"
					>
						{file.fileName}
					</a>
				) : (
					<span className="block truncate font-medium text-sm">
						{file.fileName}
					</span>
				)}
				<p className="text-muted-foreground text-xs">
					{formatFileSize(file.fileSize)}
				</p>
			</div>
			{showRemove && onRemove && (
				<Button
					className="size-6 shrink-0"
					onClick={onRemove}
					size="icon"
					variant="ghost"
				>
					<X className="size-3" />
				</Button>
			)}
		</div>
	);
}

type FileAttachmentListProps = {
	files: FileAttachmentData[];
	onRemove?: (index: number) => void;
	showRemove?: boolean;
	className?: string;
};

export function FileAttachmentList({
	files,
	onRemove,
	showRemove = false,
	className,
}: FileAttachmentListProps) {
	if (files.length === 0) return null;

	return (
		<div className={cn("mt-2 flex flex-wrap gap-2", className)}>
			{files.map((file, index) => (
				<FileAttachment
					file={file}
					key={file.objectKey || file.id || index}
					onRemove={onRemove ? () => onRemove(index) : undefined}
					showRemove={showRemove}
				/>
			))}
		</div>
	);
}
